import React, { useMemo, useState } from 'react';
import {
  FILTER_COLUMNS,
  GROUPS,
} from 'src/app/components/grid/topListHistorical/columns/columnDefs';
import { PROFILE_CONFIG } from 'src/redux/layout/topListLayoutSchema';
import profileReducer, { 
  ADD_PROFILE, 
  COPY_PROFILE,
  generateId
} from 'src/app/slicedForm/shared/reducers/profileReducer';
import {
  mapProfileStructure,
  FORM,
  PROFILE
} from 'src/app/slicedForm/mapping/mappingDirections';
import FormProvider from 'src/app/slicedForm/shared/context/FormProvider';
import Form from 'src/app/slicedForm/FilterForm/elements/Form';
import ColumnDefsProvider from 'src/app/slicedForm/shared/context/ColumnDefsProvider';
import ProfileLockedProvider from 'src/app/slicedForm/shared/context/ProfileLockedProvider';
import filterReducer from 'src/app/slicedForm/FilterForm/reducers/filterReducer';
import FormModal from 'src/app/components/FormModal';
import PropTypes from 'prop-types';
import PanelIconButton from 'src/app/components/panels/PanelIconButton';
import { FilterIcon } from 'src/theme/EdgeIcons';
import {
  makeStyles,
} from '@material-ui/core';
import { SCANNER_FUSE_OPTIONS } from 'src/app/components/grid/topListScanner/columns/defaultDefs';




const useStyles = makeStyles(() => ({
  root: {},
  relative: {
    position: 'relative'
  },
  popoverPaper: {
    maxWidth: 700,
    maxHeight: 500,
    width: '100%',
    height: '100%'
  }
}));


const serialize = (state) => mapProfileStructure(state).from(PROFILE).to(FORM);
const deserialize = (state) => mapProfileStructure(state).from(FORM).to(PROFILE);


function HistoricalSlicedFiltersForm({
  profiles,
  activeProfile,
  oldestDateAvailable,
  onSubmit,
}) {
  // const classes = useStyles();
  const [open, setOpen] = useState(false);

  const initialState = useMemo(() => {
    return { profiles, activeProfile };
  }, [profiles, activeProfile]);

  const reducers = useMemo(() => {
    return [profileReducer, filterReducer];
  }, []);

  const formActions = useMemo(() => ({
    onSubmit: ({ state }) => {
      onSubmit(deserialize(state));
      setOpen(false);
    },
    onSave: ({ state }) => {
      onSubmit(deserialize(state));
    },
    onCancel: () => {
      setOpen(false);
    },
    onCreate: ({ dispatch }) => {
      // Can't use defaults because its not serialized.
      // Just copy the serialized one.
      const idToCopy = PROFILE_CONFIG.HISTORY_FILTERS.predefinedProfiles[0].id;
      dispatch({ type: ADD_PROFILE, payload: { 
        id: idToCopy,
        newId: generateId()
      } });
    },
    onCopy: ({ state, dispatch }) => {
      const id = state?.activeProfile;
      dispatch({ type: COPY_PROFILE, payload: { 
        id,
        newId: generateId()
      } });
    }
  }), []);

  // Individual ColumnDefs can override these
  const defaultDateConfig = useMemo(() => {
    return {
      minDate: oldestDateAvailable,
      disableFuture: true,
      disableWeekend: true,
      disableHoliday: true,
      makeRollingDates: undefined,
      makeInputDates: undefined,
      makeStaticDates: undefined,
      staticRangeHeaderContent: undefined,
      format: 'yyyy-MM-dd',
      placeholder: 'yyyy-mm-dd'
    }
  }, [oldestDateAvailable])


  return (
    <>
      <PanelIconButton
        Icon={FilterIcon}
        onClick={() => setOpen(true)}
        text="Filters"
        shouldHideIconText={true}
      />
      <FormModal
        open={open}
        width={1108}
        height={900}
        onClose={formActions.onCancel}
      >
        <ColumnDefsProvider
          columnDefs={FILTER_COLUMNS}
          columnGroups={GROUPS}
          fuseOptions={SCANNER_FUSE_OPTIONS}
          defaultDateConfig={defaultDateConfig}
        >
          <FormProvider
            initialState={initialState}
            reducers={reducers}
            initFunc={serialize}
            actions={formActions}
          >
            <ProfileLockedProvider>
              <Form />
            </ProfileLockedProvider>
          </FormProvider>
        </ColumnDefsProvider>
      </FormModal>
    </>
  );
}


HistoricalSlicedFiltersForm.propTypes = {
  profiles: PropTypes.array.isRequired,
  activeProfile: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
};


export default React.memo(HistoricalSlicedFiltersForm);
